import React, { lazy, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import PrivateRoute from './components/PrivateRoute';
import NotFound from './components/NotFound';
import routes from './routeList';
import useStore from 'store';
import { Products } from '../views/products/Products';
import ProductsRouters from '../views/products/ProductsRouter';
import DocumentsRouter from '../views/Documents/DocumentsRouter';
import { useSelector } from 'react-redux';
import Login from '../views/Login/Login'
import LoginCode from '../views/LoginCode'
import Registration from '../views/Registration'
import Home from '../views/Home/Home'
import Orders from '../views/Orders/Orders'
import NewOrder from '../views/NewOrder/NewOrder'
import Suppliers from '../views/Suppliers/Suppliers'
import Users from '../views/Users/Users'
import Password from '../views/Password'
import Profile from '../views/Profile/Profile'
import RecoverPassword from '../views/RecoverPassword'
import Reports from '../views/Reports'
import Logout from '../views/Logout/Logout'
import useAuth from '../hooks/useAuth';
import Loading from '../components/Loading/Loading';
import Places from '../views/Places/PLaces';
import { ChartS } from '../views/Chart/Chart';
import MarketRoute from '../views/Marketplace/MarketRoute';
import QuotesRoute from '../views/quotes/QuotesRoute';
import SupplierCatalogRoute from '../views/SupplierCatalog/SupplierCatalogRoute';
import { flashQuote } from '../api/marcketPlace';
import toast from 'react-hot-toast';
import { getArrayOfErrors } from '../utils/errors';


// // Most important components
// const Home = lazy(() => import('../views/Home'));

// // Lazy loaded components
// const Login = lazy(() => import('../views/Login'));
// const Registration = lazy(() => import('../views/Registration'));
// const Orders = lazy(() => import('../views/Orders'));
// const NewOrder = lazy(() => import('../views/NewOrder'));
// const Suppliers = lazy(() => import('../views/Suppliers'));
// const Profile = lazy(() => import('../views/Profile'));
// const RecoverPassword = lazy(() => import('../views/RecoverPassword'));
// const Logout = lazy(() => import('../views/Logout'));
// const Users = lazy(() => import('../views/Users'));
// const Password = lazy(() => import('../views/Password'));
// const Reports = lazy(() => import('../views/Reports'));

const AppRouter = ({ restaurant, state, deleteRestaurant }) => {

  const { status, isLoadin, user, subscription, token, restaurant: restaurantId } = useSelector((state) => state.auth)
  const navigate = useNavigate();
  
  // Ref para evitar procesamiento duplicado
  const isProcessingQuote = useRef(false);
  const hasRedirectedAfterLogin = useRef(false);

  // Redirigir después del login/registro si viene del catálogo
  useEffect(() => {
    if ((status === 'authenticated' || status === 'not-complete') && !hasRedirectedAfterLogin.current) {
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      if (redirectPath) {
        hasRedirectedAfterLogin.current = true;
        sessionStorage.removeItem('redirectAfterLogin');
        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 0);
      }
    }
  }, [status, navigate]);

  // Procesar cotización pendiente después del login
  useEffect(() => {
    const processPendingQuote = async () => {
      const savedQuote = localStorage.getItem('pendingQuote');
      
      // Verificar que no se esté procesando ya y que existan todas las condiciones
      if (savedQuote && user && token && restaurantId && status === 'authenticated' && !isProcessingQuote.current) {
        
        // Marcar como procesando para evitar duplicados
        isProcessingQuote.current = true;
        
        try {
          const quoteData = JSON.parse(savedQuote);
          
          const data = [{
            supplierId: quoteData.item.supplierId.toString(),
            anonymous: quoteData.checked || false,
            note: quoteData.note || '',
            products: [{
              productId: quoteData.item.id,
              quantity: parseInt(quoteData.quantity)
            }]
          }];

          await flashQuote(token, user.id, restaurantId, data);
          
          toast.success('¡Cotización enviada exitosamente!');
          localStorage.removeItem('pendingQuote');
          
          // Redirigir al marketplace después de procesar
          setTimeout(() => {
            navigate('/marketplace');
            // Resetear la bandera después de la redirección
            isProcessingQuote.current = false;
          }, 1000);
          
        } catch (error) {
          console.error('Error procesando cotización pendiente:', error);
          if (error.response) {
            const errors = getArrayOfErrors(error.response.data);
            errors.forEach(err => toast.error(err));
          } else {
            toast.error('Error al enviar cotización pendiente');
          }
          localStorage.removeItem('pendingQuote');
          isProcessingQuote.current = false;
        }
      }
    };

    processPendingQuote();
  }, [status, user, token, restaurantId, navigate]);


  useEffect(() => {
    const redirectPath = sessionStorage.getItem('redirectAfterLogin');
    
    if (status === 'not-complete' && !redirectPath && window.location.pathname !== '/resetPassword') {
      navigate('/resetPassword');
    }
  });


  if (status === 'cheking') {
    return <Loading />
  }

  if (status === 'not-authenticated' || status === undefined) {
    return (

      <Routes>
        <Route path={routes.login} element={<Login state={state} />} />
        <Route path={routes.loginCode} element={<LoginCode />} />
        <Route path={`${routes.registration}/*`} element={<Registration />} />
        <Route path={`${routes.recoverPassword}/*`} element={<RecoverPassword />} />
        <Route path={`${routes.supplierCatalog}/*`} element={<SupplierCatalogRoute />} />
        <Route path={`${routes.marketplace}/*`} element={<MarketRoute />} />
        <Route path={'/*'} element={<Navigate to={routes.login} />} />
      </Routes>

    )
  }



  if (subscription) {

    switch (subscription.plan.abreviacion) {
      case 'gold':

        if (user && user.roles[0].name != "Staff") {
          return (
            <Routes>
              <Route path={routes.home} element={<Home restaurant={restaurant} />} />
              <Route path={`${routes.orders}/*`} element={<Orders restaurant={restaurant} />} />
              <Route path={`${routes.cotizaciones}/*`} element={<QuotesRoute />} />
              <Route path={`${routes.newOrder}/*`} element={<NewOrder />} />
              <Route path={`${routes.marketplace}/*`} element={<MarketRoute />} />
              <Route path={`${routes.supplierCatalog}/*`} element={<SupplierCatalogRoute />} />
              <Route path={`${routes.suppliers}/*`} element={<Suppliers restaurant={restaurant} />} />
              <Route path={`${routes.users}/*`} element={<Users />} />
              <Route path={`${routes.places}/*`} element={<Places />} />
              <Route path={`${routes.resetPassword}/*`} element={<Password />} />
              <Route path={`${routes.profile}/*`} element={<Profile />} />
              <Route path={`${routes.chart}/*`} element={<ChartS />} />
              <Route path={`${routes.report}/*`} element={<Reports restaurant={restaurant} />} />
              <Route path={`${routes.products}/*`} element={<ProductsRouters restaurant={restaurant} />} />
              <Route path={`${routes.documents}/*`} element={<DocumentsRouter restaurant={restaurant} />} />
              <Route path={routes.logout} element={<Logout state={state} deleteRestaurant={deleteRestaurant} />} />
              <Route path={`${routes.resetPassword}/*`} element={<Password />} />
              <Route path={'/*'} element={<Navigate to={routes.home} />} />
            </Routes>
          )
        } else {
          return (
            <Routes>
              <Route path={routes.home} element={<Home restaurant={restaurant} />} />
              <Route path={`${routes.marketplace}/*`} element={<MarketRoute />} />
              <Route path={`${routes.supplierCatalog}/*`} element={<SupplierCatalogRoute />} />
              <Route path={`${routes.cotizaciones}/*`} element={<QuotesRoute />} />
              <Route path={routes.logout} element={<Logout state={state} deleteRestaurant={deleteRestaurant} />} />
              <Route path={`${routes.orders}/*`} element={<Orders restaurant={restaurant} />} />
              <Route path={`${routes.resetPassword}/*`} element={<Password />} />
              <Route path={`${routes.profile}/*`} element={<Profile />} />
              <Route path={`${routes.users}/*`} element={<Users />} />
              <Route path={`${routes.places}/*`} element={<Places />} />
              <Route path={'/*'} element={<Navigate to={routes.home} />} />
            </Routes>

          )

        }

      case "estandar":

        if (user && user.roles[0].name != "Staff") {
          return (
            <Routes>
              <Route path={routes.home} element={<Home restaurant={restaurant} />} />
              <Route path={`${routes.orders}/*`} element={<Orders restaurant={restaurant} />} />
              <Route path={`${routes.cotizaciones}/*`} element={<QuotesRoute />} />
              <Route path={`${routes.marketplace}/*`} element={<MarketRoute />} />
              <Route path={`${routes.supplierCatalog}/*`} element={<SupplierCatalogRoute />} />
              <Route path={`${routes.newOrder}/*`} element={<NewOrder />} />
              <Route path={`${routes.suppliers}/*`} element={<Suppliers restaurant={restaurant} />} />
              <Route path={`${routes.users}/*`} element={<Users />} />
              <Route path={`${routes.places}/*`} element={<Places />} />
              <Route path={`${routes.resetPassword}/*`} element={<Password />} />
              <Route path={`${routes.profile}/*`} element={<Profile />} />
              <Route path={`${routes.report}/*`} element={<Reports restaurant={restaurant} />} />
              <Route path={`${routes.products}/*`} element={<ProductsRouters restaurant={restaurant} />} />
              <Route path={`${routes.documents}/*`} element={<DocumentsRouter restaurant={restaurant} />} />
              <Route path={`${routes.chart}/*`} element={<ChartS />} />
              <Route path={routes.logout} element={<Logout state={state} deleteRestaurant={deleteRestaurant} />} />
              <Route path={'/*'} element={<Navigate to={routes.home} />} />
            </Routes>
          )
        } else {
          return (
            <Routes>
              <Route path={routes.home} element={<Home restaurant={restaurant} />} />
              <Route path={routes.logout} element={<Logout state={state} deleteRestaurant={deleteRestaurant} />} />
              <Route path={`${routes.supplierCatalog}/*`} element={<SupplierCatalogRoute />} />
              <Route path={`${routes.marketplace}/*`} element={<MarketRoute />} />
              <Route path={`${routes.cotizaciones}/*`} element={<QuotesRoute />} />
              <Route path={`${routes.orders}/*`} element={<Orders restaurant={restaurant} />} />
              <Route path={`${routes.resetPassword}/*`} element={<Password />} />
              <Route path={`${routes.profile}/*`} element={<Profile />} />
              <Route path={`${routes.users}/*`} element={<Users />} />
              <Route path={`${routes.places}/*`} element={<Places />} />
              <Route path={'/*'} element={<Navigate to={routes.home} />} />
            </Routes>
          )
        }

      case "free":

        if (user && user.roles[0].name != "Staff") {
          return (
            <Routes>
              <Route path={routes.home} element={<Home restaurant={restaurant} />} />
              <Route path={`${routes.orders}/*`} element={<Orders restaurant={restaurant} />} />
              <Route path={`${routes.marketplace}/*`} element={<MarketRoute />} />
              <Route path={`${routes.supplierCatalog}/*`} element={<SupplierCatalogRoute />} />
              <Route path={`${routes.cotizaciones}/*`} element={<QuotesRoute />} />
              <Route path={`${routes.newOrder}/*`} element={<NewOrder />} />
              <Route path={`${routes.suppliers}/*`} element={<Suppliers restaurant={restaurant} />} />
              <Route path={`${routes.users}/*`} element={<Users />} />
              <Route path={`${routes.places}/*`} element={<Places />} />
              <Route path={`${routes.resetPassword}/*`} element={<Password />} />
              <Route path={`${routes.profile}/*`} element={<Profile />} />
              <Route path={`${routes.report}/*`} element={<Reports restaurant={restaurant} />} />
              <Route path={`${routes.products}/*`} element={<ProductsRouters restaurant={restaurant} />} />
              <Route path={`${routes.documents}/*`} element={<DocumentsRouter restaurant={restaurant} />} />
              <Route path={routes.logout} element={<Logout state={state} deleteRestaurant={deleteRestaurant} />} />
              <Route path={'/*'} element={<Navigate to={routes.home} />} />
            </Routes>
          )
        } else {
          return (
            <Routes>
              <Route path={routes.home} element={<Home restaurant={restaurant} />} />
              <Route path={routes.logout} element={<Logout state={state} deleteRestaurant={deleteRestaurant} />} />
              <Route path={`${routes.marketplace}/*`} element={<MarketRoute />} />
              <Route path={`${routes.supplierCatalog}/*`} element={<SupplierCatalogRoute />} />
              <Route path={`${routes.cotizaciones}/*`} element={<QuotesRoute />} />
              <Route path={`${routes.orders}/*`} element={<Orders restaurant={restaurant} />} />
              <Route path={`${routes.resetPassword}/*`} element={<Password />} />
              <Route path={`${routes.profile}/*`} element={<Profile />} />
              <Route path={`${routes.users}/*`} element={<Users />} />
              <Route path={`${routes.places}/*`} element={<Places />} />
              <Route path={'/*'} element={<Navigate to={routes.home} />} />
            </Routes>
          )
        }
      
      default:
        null;
    }
  } else {
    return (
      <Routes>
        <Route path={routes.home} element={<Home restaurant={restaurant} />} />
        <Route path={`${routes.orders}/*`} element={<Orders restaurant={restaurant} />} />
        <Route path={`${routes.supplierCatalog}/*`} element={<SupplierCatalogRoute />} />
        <Route path={`${routes.newOrder}/*`} element={<NewOrder />} />
        <Route path={`${routes.suppliers}/*`} element={<Suppliers restaurant={restaurant} />} />
        <Route path={`${routes.users}/*`} element={<Users />} />
        <Route path={`${routes.places}/*`} element={<Places />} />
        <Route path={`${routes.resetPassword}/*`} element={<Password />} />
        <Route path={`${routes.profile}/*`} element={<Profile />} />
        <Route path={`${routes.report}/*`} element={<Reports restaurant={restaurant} />} />
        <Route path={`${routes.products}/*`} element={<ProductsRouters restaurant={restaurant} />} />
        <Route path={`${routes.documents}/*`} element={<DocumentsRouter restaurant={restaurant} />} />
        <Route path={routes.logout} element={<Logout state={state} deleteRestaurant={deleteRestaurant} />} />
        <Route path={'/*'} element={<Navigate to={routes.home} />} />
      </Routes>
    )
  }

};

export default AppRouter;